import React, {useContext, useEffect, useMemo, useState} from "react";
import {Button, Card, Input, Popconfirm, Select, Space, Tooltip, Typography, message} from "antd";
import {PlusOutlined, SaveOutlined, DeleteOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {useSelector} from "react-redux";
import {AuthContext} from "../Auth/AuthProvider";
import {smileConcealAPIcall} from "../util/redux/asyncActions";
import ProfileEditor from "./ProfileEditor";

const {Text} = Typography;

/**
 * Detection Profiles: named DELTAS on the default config (e.g. "internal" vs
 * "external" worklog rules). The form shows effective values based on the
 * default config; only deviations are stored. Anonymizer entries reference
 * profiles by name.
 */
const ProfilesTab = () => {
    const userManager = useContext(AuthContext);
    const config = useSelector((state) => state.request.config);

    const defaultConfig = useMemo(() => {
        try {
            return config ? JSON.parse(config) : {};
        } catch (e) {
            return {};
        }
    }, [config]);

    const [profileNames, setProfileNames] = useState([]);
    const [selected, setSelected] = useState("");
    const [delta, setDelta] = useState({});
    const [newName, setNewName] = useState("");
    const [saving, setSaving] = useState(false);

    const api = (path, options) => smileConcealAPIcall({
        url: `${window._env_.REACT_APP_API_URL}${path}`,
        requestOptions: {headers: {"content-type": "application/json"}, ...options},
        userManager
    });

    const refresh = () => api("/profiles", {method: "GET"})
        .then((r) => setProfileNames((r && r.profiles) || []))
        .catch(() => setProfileNames([]));

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = (name) => {
        setSelected(name);
        api(`/profiles/${encodeURIComponent(name)}`, {method: "GET"})
            .then((r) => setDelta((r && r.delta) || {}))
            .catch(() => setDelta({}));
    };

    const save = () => {
        setSaving(true);
        api(`/profiles/${encodeURIComponent(selected)}`, {method: "POST", body: JSON.stringify({delta})})
            .then(() => message.success(`Profile "${selected}" saved and active`))
            .catch((e) => message.error("Save failed: " + e))
            .finally(() => setSaving(false));
    };

    const create = () => {
        const name = newName.trim();
        api(`/profiles/${encodeURIComponent(name)}`, {method: "POST", body: JSON.stringify({delta: {}})})
            .then(() => {
                setNewName("");
                refresh().then(() => load(name));
            })
            .catch((e) => message.error("Create failed: " + e));
    };

    const remove = () => {
        api(`/profiles/${encodeURIComponent(selected)}`, {method: "DELETE"})
            .then(() => {
                setSelected("");
                setDelta({});
                refresh();
            })
            .catch((e) => message.error("Delete failed: " + e));
    };

    const overrideCount = Object.keys(delta || {}).length;

    return (
        <div style={{maxWidth: 960, margin: "0 auto", padding: "24px 32px"}}>
            <Card
                title={
                    <Space>
                        Detection Profiles
                        <Tooltip title="Named deltas on the default config. A profile only stores what differs - everything else automatically follows the default (a new pattern in the default applies to all profiles). Anonymizer 'Remove PII' entries pick a profile per entry.">
                            <QuestionCircleOutlined style={{color: "#bfbfbf"}}/>
                        </Tooltip>
                    </Space>
                }
            >
                <Space style={{marginBottom: 16}} wrap>
                    <Select
                        style={{width: 220}}
                        placeholder="Select profile"
                        value={selected || undefined}
                        onChange={load}
                        options={profileNames.map((n) => ({value: n, label: n}))}
                    />
                    <Input
                        placeholder="new profile name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onPressEnter={create}
                        style={{width: 180}}
                    />
                    <Button icon={<PlusOutlined/>} onClick={create} disabled={!newName.trim()}>Create</Button>
                    {selected && (
                        <>
                            <Button type="primary" icon={<SaveOutlined/>} onClick={save} loading={saving}>
                                Save profile
                            </Button>
                            <Popconfirm title={`Delete profile "${selected}"?`} onConfirm={remove}>
                                <Button danger icon={<DeleteOutlined/>}/>
                            </Popconfirm>
                            <Text type="secondary" style={{fontSize: 12}}>
                                {overrideCount === 0
                                    ? "no overrides - behaves exactly like the default"
                                    : `${overrideCount} override${overrideCount > 1 ? "s" : ""} vs. default`}
                            </Text>
                        </>
                    )}
                </Space>

                {selected ? (
                    <ProfileEditor delta={delta} defaultConfig={defaultConfig} onChange={setDelta}/>
                ) : (
                    <Text type="secondary">
                        Select a profile or create one - e.g. "internal" (agent names stay readable)
                        and "external" (strictest rules) to run different detection per worklog class.
                    </Text>
                )}
            </Card>
        </div>
    );
};

export default ProfilesTab;
